import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { User } from "@/types/user";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { buildSsoLoginRedirectUrl } from "@/utils/ssoErrors";
import { ACCOUNT_DEACTIVATED_SESSION_KEY } from "@/routes/guards/AccountDeactivatedGuard";

interface SsoCallbackParams {
  code: string;
  // Test-SSO round-trip carries `state="test_sso:<org_id>:<actor_user_id>"`.
  // Forwarded verbatim so the backend returns a `test_result` payload
  // instead of minting a session.
  state?: string;
}

interface NoWorkspaceAccessContact {
  organization_id: number;
  organization_name: string;
  owner_email: string;
  owner_name: string | null;
}

interface SsoCallbackResponse {
  access_token?: string;
  user?: User;
  redirect_to?: string;
  no_workspace_access_contact?: NoWorkspaceAccessContact;
  // Present when the callback resolved a Test-SSO round-trip; no JWT
  // is minted in that case.
  test_result?: "succeeded" | "failed";
  organization_id?: number;
  profile_email?: string;
  reason?: string;
  error?: string;
}

// Whitelist of routes the backend is allowed to ask the frontend to
// redirect to from the SSO callback. The backend already returns a
// hardcoded string today, but this guard means a future bug there
// can't be turned into an open-redirect on this side.
const ALLOWED_SSO_REDIRECTS = new Set(["/no-workspace-access", "/account-deactivated"]);

export const useSsoCallback = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  return useMutation(
    ["sso-callback"],
    async (params: SsoCallbackParams): Promise<SsoCallbackResponse> =>
      fetch(ENDPOINTS.auth.sso_callback, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: params.code,
          ...(params.state ? { state: params.state } : {}),
        }),
      }).then(handleResponse),
    {
      onSuccess: ({
        access_token,
        user,
        redirect_to,
        no_workspace_access_contact,
        test_result,
        profile_email,
        reason,
      }) => {
        // Test-SSO round-trip: backend already audited the result and
        // returned no access_token. Navigate the popup to the result
        // page with the outcome in the query string.
        if (test_result) {
          const params = new URLSearchParams();
          params.set("test_result", test_result);
          if (profile_email) {
            params.set("email", profile_email);
          }
          if (reason) {
            params.set("reason", reason);
          }
          // Top-level route in LoginLayout — the popup can't reach
          // workspace-scoped routes without a `/workspace/:id` prefix.
          navigate(`/auth/sso/test-result?${params.toString()}`);
          return;
        }
        // Soft-deleted identities are surfaced via a 403 that
        // handleResponse routes to onError, so only access_token
        // present flows reach this branch (regular sign-in or the
        // JIT no-workspace-access redirect).
        if (access_token && user) {
          auth?.login(access_token, user);
          if (redirect_to && ALLOWED_SSO_REDIRECTS.has(redirect_to)) {
            const params = new URLSearchParams();
            if (no_workspace_access_contact) {
              params.set("org", no_workspace_access_contact.organization_name);
              params.set("email", no_workspace_access_contact.owner_email);
              if (no_workspace_access_contact.owner_name) {
                params.set("name", no_workspace_access_contact.owner_name);
              }
            }
            const query = params.toString();
            navigate(query ? `${redirect_to}?${query}` : redirect_to);
            return;
          }
          navigate("/home");
        }
      },
      onError: (err: unknown) => {
        const redirectToLoginWithMessage = (message: string) => {
          navigate(buildSsoLoginRedirectUrl(message));
        };

        // Soft-deleted identities arrive as a 403 whose body includes
        // `redirect_to: "/account-deactivated"`. Route there instead of
        // leaving the callback spinner up with a generic toast.
        if (typeof err === "object" && err !== null) {
          const structured = err as {
            message?: string;
            code?: string;
            redirect_to?: string;
          };
          if (structured.redirect_to && ALLOWED_SSO_REDIRECTS.has(structured.redirect_to)) {
            if (structured.redirect_to === "/account-deactivated") {
              sessionStorage.setItem(ACCOUNT_DEACTIVATED_SESSION_KEY, "1");
            }
            navigate(structured.redirect_to);
            return;
          }
          if (structured.code === "reactivation_rejected") {
            sessionStorage.setItem(ACCOUNT_DEACTIVATED_SESSION_KEY, "1");
            navigate("/account-deactivated");
            return;
          }
          redirectToLoginWithMessage(structured.message || "Failed to authenticate with SSO.");
          return;
        }

        const message = typeof err === "string" ? err : "";
        if (message.includes("/account-deactivated")) {
          sessionStorage.setItem(ACCOUNT_DEACTIVATED_SESSION_KEY, "1");
          navigate("/account-deactivated");
          return;
        }
        redirectToLoginWithMessage(message || "Failed to authenticate with SSO.");
      },
    },
  );
};
